import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Input,
  Snackbar,
  FormControlLabel,
  Switch,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { auth } from '../utils/auth';
import ReactMarkdown from 'react-markdown';
import { 
  CloudUpload, 
  Description, 
  Settings, 
  Help, 
  Edit, 
  SaveAlt, 
  Lock, 
  LockOpen,
  Info,
  CompareArrows,
  Speed,
  Storage,
  Timer,
  FormatListBulleted,
  FileDownload,
  Group,
  History,
  Undo,
  Redo
} from '@mui/icons-material';
import { generateContent } from '../utils/ai';
import axios from 'axios';
import { saveNote, getNotes } from '../utils/supabase';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import JSZip from 'jszip';
import { getDocument } from 'pdfjs-dist';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

function NotesGenerator() {
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [user, setUser] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showPromptSettings, setShowPromptSettings] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [originalNotes, setOriginalNotes] = useState('');
  const [isPro, setIsPro] = useState(() => {
    const savedProStatus = localStorage.getItem('notesGeneratorPro');
    return savedProStatus === 'true';
  });
  const [showFeatureComparison, setShowFeatureComparison] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [noteTemplate, setNoteTemplate] = useState('default');
  const [exportFormat, setExportFormat] = useState('markdown');
  const [editHistory, setEditHistory] = useState([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(-1);

  useEffect(() => {
    const initUser = async () => {
      const currentUser = await auth.getCurrentUser();
      setUser(currentUser);
      if (currentUser) {
        try {
          const savedNotes = await getNotes(currentUser.id);
          if (savedNotes && savedNotes.length > 0) {
            setNotes(savedNotes[0].content);
            setOriginalNotes(savedNotes[0].content);
          }
        } catch (error) {
          console.error('Error loading notes:', error);
          setError('Failed to load saved notes');
        }
      }
    };
    initUser();
  }, []);

  const handleFileUpload = (event) => {
    const uploadedFiles = event.target.files;
    if (uploadedFiles.length > 1 && isPro) {
      handleBatchProcess(Array.from(uploadedFiles));
    } else {
      const uploadedFile = uploadedFiles[0];
      if (uploadedFile) {
        if (uploadedFile.type === 'application/pdf' || uploadedFile.type === 'text/plain') {
          const maxFileSize = isPro ? 500 * 1024 * 1024 : 10 * 1024 * 1024;
          if (uploadedFile.size > maxFileSize) {
            setError(`File size exceeds the limit. ${isPro ? 'Pro users can upload up to 500MB.' : 'Free users are limited to 10MB.'}`);
            return;
          }
          setFile(uploadedFile);
          setError('');
        } else {
          setError('Please upload a PDF or text file');
        }
      }
    }
  };

  const handleGenerateNotes = async () => {
    if (!file && !text.trim()) {
      setError('Please upload a file or enter text');
      return;
    }

    setLoading(true);
    setError('');
    setProcessingProgress(0);

    try {
      let content = text;

      if (file) {
        if (file.type === 'text/plain') {
          content = await file.text();
        } else if (file.type === 'application/pdf') {
          const pdf = await import('pdfjs-dist');
          pdf.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).href;
          const arrayBuffer = await file.arrayBuffer();
          const loadingTask = pdf.getDocument({ data: arrayBuffer });
          const pdfDoc = await loadingTask.promise;
          let extractedText = '';

          const processPage = async (pageNum) => {
            const page = await pdfDoc.getPage(pageNum);
            const textContent = await page.getTextContent();
            setProcessingProgress((pageNum / pdfDoc.numPages) * 100);
            return textContent.items.map(item => item.str).join(' ');
          };

          if (isPro) {
            const pagePromises = Array.from({ length: pdfDoc.numPages }, (_, i) => processPage(i + 1));
            const pageTexts = await Promise.all(pagePromises);
            extractedText = pageTexts.join(' ');
          } else {
            for (let i = 1; i <= pdfDoc.numPages; i++) {
              extractedText += await processPage(i);
            }
          }
          content = extractedText;
        }
      }

      if (!content.trim()) {
        setError('No content to process');
        return;
      }

      const systemPrompt = useCustomPrompt && customPrompt.trim() 
        ? customPrompt.trim() 
        : getTemplatePrompt(noteTemplate);

      const messages = [
        {
          role: "system",
          content: systemPrompt
        },
        {
          role: "user",
          content: content
        }
      ];

      const notes = await generateContent(messages);
      setNotes(notes);
      setOriginalNotes(notes);
      if (auth.currentUser) {
        await saveNote(auth.currentUser.uid, notes);
      }
      setProcessingProgress(100);
    } catch (err) {
      console.error('Note generation error:', err);
      setError(`Failed to generate notes: ${err.message}`);
    } finally {
      setLoading(false);
      setProcessingProgress(0);
    }
  };

  const handleExport = async () => {
    if (!notes) return;

    try {
      let content = notes;
      let mimeType = 'text/plain';
      let extension = '.txt';

      switch (exportFormat) {
        case 'markdown':
          mimeType = 'text/markdown';
          extension = '.md';
          break;
        case 'pdf':
          // Create a simple PDF using a canvas
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          const pageWidth = 595.28; // A4 width in points
          const pageHeight = 841.89; // A4 height in points
          const margin = 40;
          const lineHeight = 20;
          
          // Set canvas size
          canvas.width = pageWidth;
          canvas.height = pageHeight;
          
          // Fill background
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, pageWidth, pageHeight);
          
          // Set text style
          context.fillStyle = '#000000';
          context.font = '12px Arial';
          
          // Split text into lines and draw
          const lines = notes.split('\n');
          let y = margin;
          
          for (const line of lines) {
            if (y > pageHeight - margin) {
              // Create new page
              y = margin;
            }
            context.fillText(line, margin, y);
            y += lineHeight;
          }
          
          // Convert canvas to blob
          const pdfBlob = await new Promise(resolve => canvas.toBlob(resolve, 'application/pdf'));
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const pdfLink = document.createElement('a');
          pdfLink.href = pdfUrl;
          pdfLink.download = 'notes.pdf';
          document.body.appendChild(pdfLink);
          pdfLink.click();
          document.body.removeChild(pdfLink);
          URL.revokeObjectURL(pdfUrl);
          setSnackbarMessage('Notes exported as PDF!');
          setSnackbarOpen(true);
          return;
        case 'docx':
          // Create a new document with proper formatting
          const docxDoc = new Document({
            sections: [{
              properties: {},
              children: notes.split('\n').map(line => {
                // Skip empty lines
                if (!line.trim()) return null;
                
                // Check if line is a heading (starts with #)
                const headingMatch = line.match(/^(#+)\s*(.+)/);
                if (headingMatch) {
                  const level = headingMatch[1].length;
                  return new Paragraph({
                    children: [new TextRun({
                      text: headingMatch[2],
                      bold: true,
                      size: 24 - (level * 2) + 'pt'
                    })],
                    spacing: { after: 200 }
                  });
                }
                
                // Regular paragraph
                return new Paragraph({
                  children: [new TextRun(line)],
                  spacing: { after: 100 }
                });
              }).filter(Boolean), // Remove null entries
            }],
          });
          
          const buffer = await Packer.toBlob(docxDoc);
          const docxUrl = URL.createObjectURL(buffer);
          const docxLink = document.createElement('a');
          docxLink.href = docxUrl;
          docxLink.download = 'notes.docx';
          document.body.appendChild(docxLink);
          docxLink.click();
          document.body.removeChild(docxLink);
          URL.revokeObjectURL(docxUrl);
          setSnackbarMessage('Notes exported as Word document!');
          setSnackbarOpen(true);
          return;
        case 'html':
          mimeType = 'text/html';
          extension = '.html';
          content = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="UTF-8">
                <title>Generated Notes</title>
                <style>
                  body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; max-width: 800px; margin: 0 auto; }
                  h1, h2, h3, h4, h5, h6 { color: #333; margin-top: 1.5em; }
                  p { margin: 1em 0; }
                </style>
              </head>
              <body>
                ${notes}
              </body>
            </html>
          `;
          break;
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `notes${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbarMessage('Notes exported successfully!');
      setSnackbarOpen(true);
        } catch (err) {
      console.error('Export error:', err);
      setError(`Failed to export notes: ${err.message}`);
    }
  };

  const handleBatchProcess = async (files) => {
    if (!isPro) {
      setError('Batch processing is a pro feature');
      return;
    }

    setLoading(true);
    setError('');
    setProcessingProgress(0);

    try {
      const results = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProcessingProgress((i / files.length) * 100);
        
        let content = '';
        if (file.type === 'text/plain') {
          content = await file.text();
        } else if (file.type === 'application/pdf') {
          const pdf = await import('pdfjs-dist');
          pdf.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).href;
          const arrayBuffer = await file.arrayBuffer();
          const loadingTask = pdf.getDocument({ data: arrayBuffer });
          const pdfDoc = await loadingTask.promise;
          
          const pagePromises = Array.from({ length: pdfDoc.numPages }, (_, i) => {
            return pdfDoc.getPage(i + 1).then(page => {
              return page.getTextContent().then(textContent => {
                return textContent.items.map(item => item.str).join(' ');
              });
            });
          });
          
          const pageTexts = await Promise.all(pagePromises);
          content = pageTexts.join(' ');
        }

        const notes = await generateNotes(content, 'text');
        results.push({
          filename: file.name,
          content: notes
        });
      }

      // Create a zip file with all results
      const zip = new JSZip();
      results.forEach(result => {
        zip.file(`${result.filename}.md`, result.content);
      });
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = URL.createObjectURL(zipBlob);
      const zipLink = document.createElement('a');
      zipLink.href = zipUrl;
      zipLink.download = 'batch_notes.zip';
      document.body.appendChild(zipLink);
      zipLink.click();
      document.body.removeChild(zipLink);
      URL.revokeObjectURL(zipUrl);
      
      setSnackbarMessage('Batch processing completed!');
      setSnackbarOpen(true);
    } catch (err) {
      setError(`Batch processing failed: ${err.message}`);
    } finally {
      setLoading(false);
      setProcessingProgress(0);
    }
  };

  const handleEditNotes = () => {
    setEditPrompt('');
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditPrompt('');
  };

  const handleEditNotesSubmit = async () => {
    if (!editPrompt.trim()) {
      return;
    }

    setEditingNotes(true);
    setEditDialogOpen(false);

    try {
      const messages = [
        {
          role: "system",
          content: `You are an expert at editing and improving notes. Follow the user's instructions carefully to modify the provided notes. 
          Consider the previous edits and maintain consistency with the existing content.`
        },
        {
          role: "user",
          content: `Current notes:\n${notes}\n\nPrevious edits:\n${editHistory.slice(-3).join('\n\n')}\n\nEdit instructions: ${editPrompt}`
        }
      ];

      const editedNotes = await generateContent(messages);
      
      // Update edit history
      setEditHistory(prev => [...prev, {
        timestamp: new Date().toISOString(),
        prompt: editPrompt,
        notes: editedNotes
      }]);
      setCurrentEditIndex(editHistory.length);

      setNotes(editedNotes);
      setSnackbarMessage('Notes edited successfully!');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Edit error:', err);
      setError(`Failed to edit notes: ${err.message}`);
    } finally {
      setEditingNotes(false);
    }
  };

  const handleUndoEdit = () => {
    if (currentEditIndex > 0) {
      const previousEdit = editHistory[currentEditIndex - 1];
      setNotes(previousEdit.notes);
      setCurrentEditIndex(currentEditIndex - 1);
      setSnackbarMessage('Previous edit restored');
      setSnackbarOpen(true);
    }
  };

  const handleRedoEdit = () => {
    if (currentEditIndex < editHistory.length - 1) {
      const nextEdit = editHistory[currentEditIndex + 1];
      setNotes(nextEdit.notes);
      setCurrentEditIndex(currentEditIndex + 1);
      setSnackbarMessage('Next edit restored');
      setSnackbarOpen(true);
    }
  };

  const handleRevertChanges = () => {
    setNotes(originalNotes);
    setSnackbarMessage('Reverted to original notes');
    setSnackbarOpen(true);
  };

  const getTemplatePrompt = (template) => {
    switch (template) {
      case 'detailed':
        return `You are an expert at creating detailed, comprehensive notes. Follow these formatting rules exactly:

1. Start with a main title (#) followed by a brief overview
2. Create major sections (##) for each main topic
3. Use sub-sections (###) for detailed concepts
4. For each concept:
   - Use bullet points (-) for main points
   - Use sub-bullets (*) for details
   - Use bold (**) for key terms
   - Use italic (*) for examples
5. Add numbered lists for step-by-step processes
6. Include code blocks (\`\`\`) for technical terms
7. Use blockquotes (>) for important quotes
8. Add tables for comparative information
9. Use emojis for visual organization:
   - 🔑 for key concepts
   - 💡 for insights
   - ⚠️ for warnings
   - 📝 for examples
10. End with a summary section

Format the output in clean, well-structured markdown with proper spacing and hierarchy.`;

      case 'concise':
        return `You are an expert at creating concise, focused notes. Follow these formatting rules exactly:

1. Start with a single main title (#)
2. Use minimal sub-headings (##) only for major topics
3. For each topic:
   - Use bullet points (-) for key points
   - Keep each point to 1-2 lines maximum
   - Use bold (**) for important terms only
4. Use numbered lists (1.) for sequences
5. Add emojis for quick scanning:
   - 📌 for main points
   - 🔍 for details
   - ⚡ for quick tips
6. Use tables for quick comparisons
7. Include only essential information
8. Use abbreviations where appropriate
9. Add a brief summary at the end
10. Keep language simple and direct

Format the output in clean, minimal markdown with consistent spacing.`;

      case 'question':
        return `You are an expert at creating question-based notes. Follow these formatting rules exactly:

1. Start with a main question (#)
2. Follow with a detailed answer (>)
3. For each major concept:
   - Create a sub-question (##)
   - Provide a detailed answer (>)
   - Use bullet points (-) for supporting points
4. Use bold (**) for key terms in questions
5. Use italic (*) for examples in answers
6. Add "Key Points" sections with bullet points
7. Include "Common Mistakes" sections
8. Add "Practice Questions" at the end
9. Use emojis for organization:
   - ❓ for questions
   - ✅ for answers
   - 💡 for tips
   - ⚠️ for warnings
10. End with a summary of key questions

Format the output in clear Q&A sections with proper spacing.`;

      case 'mindmap':
        return `You are an expert at creating mindmap-style notes. Follow these formatting rules exactly:

1. Start with central topic (#)
2. Create main branches (##)
3. Add sub-branches (###)
4. For each branch:
   - Use bullet points (-) for details
   - Use indentation for hierarchy
   - Use bold (**) for main concepts
   - Use italic (*) for explanations
5. Add connections between concepts (→)
6. Include examples in parentheses
7. Use emojis for visual organization:
   - 🌳 for main branches
   - 🌿 for sub-branches
   - 🍃 for details
   - 🔗 for connections
8. Add color coding using emojis
9. Include cross-references
10. End with a summary of connections

Format the output in hierarchical markdown with clear visual structure.`;

      case 'flashcard':
        return `You are an expert at creating flashcard-style notes. Follow these formatting rules exactly:

1. Start each card with a clear question (#)
2. Follow with the answer (>)
3. Add key points (-)
4. Include examples (*)
5. Use bold (**) for important terms
6. Add "Related Concepts" sections
7. Include "Common Mistakes"
8. Add "Memory Tips"
9. Use emojis for organization:
   - 🎴 for cards
   - 💡 for tips
   - ⚠️ for warnings
   - 🔄 for related concepts
10. End with a summary section

Format the output in clear, card-like sections with proper spacing.`;

      case 'summary':
        return `You are an expert at creating executive summaries. Follow these formatting rules exactly:

1. Start with main points (#)
2. Use bullet points (-) for key takeaways
3. Include sub-points (*)
4. Use bold (**) for critical information
5. Add "Key Findings" sections
6. Include "Recommendations"
7. Use tables for quick comparisons
8. Add "Next Steps" section
9. Use emojis for emphasis:
   - 📌 for main points
   - 🎯 for goals
   - ⚡ for actions
10. Keep language concise and professional

Format the output in clean, professional markdown with clear hierarchy.`;

      case 'cornell':
        return `You are an expert at creating Cornell-style notes. Follow these formatting rules exactly:

1. Divide content into three sections:
   - Main Notes (right side)
   - Cues/Questions (left side)
   - Summary (bottom)
2. For main notes:
   - Use headings (#) for topics
   - Use bullet points (-) for details
   - Use bold (**) for key terms
3. For cues/questions:
   - Use italic (*) for questions
   - Use bullet points (-) for cues
4. For summary:
   - Use bullet points (-)
   - Keep it concise
5. Add examples in parentheses
6. Include diagrams where relevant
7. Use tables for comparisons
8. Add a clear summary section
9. Use emojis for organization:
   - 📝 for notes
   - ❓ for questions
   - 📌 for summary
10. Maintain clear section separation

Format the output in clear, three-section markdown with proper spacing.`;

      case 'outline':
        return `You are an expert at creating detailed outlines. Follow these formatting rules exactly:

1. Use hierarchical numbering:
   - I. for main topics
   - A. for subtopics
   - 1. for points
   - a. for sub-points
2. Use proper indentation
3. Use bold (**) for main topics
4. Use italic (*) for subtopics
5. Include examples in parentheses
6. Add key terms in bold
7. Use bullet points (-) for details
8. Include cross-references
9. Add summary points
10. Use emojis for emphasis:
   - 📑 for main points
   - 📌 for details
   - 🔗 for references

Format the output in clear, hierarchical markdown with proper numbering.`;

      default:
        return `You are an expert at creating well-structured notes. Follow these formatting rules exactly:

1. Use clear headings (#, ##, ###)
2. Use bullet points (-) for main points
3. Use sub-bullets (*) for details
4. Use bold (**) for important terms
5. Use italic (*) for examples
6. Include numbered lists for sequences
7. Add tables for comparisons
8. Use blockquotes (>) for quotes
9. Include code blocks for technical terms
10. Use emojis for visual organization

Format the output in clean, well-structured markdown with proper spacing and hierarchy.`;
    }
  };

  const defaultPrompt = 'You are an expert at converting text into well-structured notes. Format the notes with proper headings, bullet points, and highlight key concepts.';

  const noteTemplates = [
    { value: 'default', label: 'Default Format', icon: '📝' },
    { value: 'detailed', label: 'Detailed Notes', icon: '📚' },
    { value: 'concise', label: 'Concise Notes', icon: '✂️' },
    { value: 'question', label: 'Q&A Format', icon: '❓' },
    { value: 'mindmap', label: 'Mind Map', icon: '🗺️' },
    { value: 'flashcard', label: 'Flashcards', icon: '🎴' },
    { value: 'summary', label: 'Executive Summary', icon: '📋' },
    { value: 'cornell', label: 'Cornell Notes', icon: '📑' },
    { value: 'outline', label: 'Detailed Outline', icon: '📊' }
  ];

  const exportFormats = [
    { value: 'markdown', label: 'Markdown' },
    { value: 'pdf', label: 'PDF' },
    { value: 'docx', label: 'Word Document' },
    { value: 'html', label: 'HTML' }
  ];

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h2" gutterBottom>
            AI Notes Generator
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<CompareArrows />}
              onClick={() => setShowFeatureComparison(true)}
            >
              Compare Features
            </Button>
            <Button
              variant="outlined"
              color={isPro ? "success" : "primary"}
              startIcon={isPro ? <LockOpen /> : <Lock />}
            >
              {isPro ? "Pro Features Active" : "Free Version"}
            </Button>
          </Stack>
        </Box>

        {isPro && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Chip icon={<FileDownload />} label="Pro Features Active" color="success" />
            </Stack>
          </Box>
        )}

        {loading && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress variant="determinate" value={processingProgress} />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Processing... {Math.round(processingProgress)}%
            </Typography>
          </Box>
        )}

        <Stack spacing={4}>
          {/* File Upload Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Document
            </Typography>
            <Stack spacing={2}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ width: 'fit-content' }}
              >
                Upload File
                <Input
                  type="file"
                  onChange={handleFileUpload}
                  sx={{ display: 'none' }}
                  accept=".pdf,.txt"
                />
              </Button>
              {file && (
                <Typography variant="body2" color="text.secondary">
                  Selected file: {file.name}
                </Typography>
              )}
            </Stack>
          </Paper>

          {/* Text Input Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Or Enter Text
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Enter your text here..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={loading}
            />
          </Paper>

          {/* Pro Features Section */}
          {isPro && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Pro Features
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Note Template</InputLabel>
                  <Select
                    value={noteTemplate}
                    label="Note Template"
                    onChange={(e) => setNoteTemplate(e.target.value)}
                  >
                    {noteTemplates.map((template) => (
                      <MenuItem key={template.value} value={template.value}>
                        {template.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Export Format</InputLabel>
                  <Select
                    value={exportFormat}
                    label="Export Format"
                    onChange={(e) => setExportFormat(e.target.value)}
                  >
                    {exportFormats.map((format) => (
                      <MenuItem key={format.value} value={format.value}>
                        {format.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Paper>
          )}

          {/* Prompt Settings */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" display="flex" alignItems="center">
                Custom Prompt Settings
                <Tooltip title="Use custom prompts to control how your notes are generated. For example: 'Create notes in Cornell format' or 'Generate flashcard-style notes with questions and answers'">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <Help fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <IconButton 
                onClick={() => setShowPromptSettings(!showPromptSettings)}
                sx={{ transform: showPromptSettings ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}
              >
                <Settings />
              </IconButton>
            </Box>
            
            <Collapse in={showPromptSettings}>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={useCustomPrompt} 
                      onChange={(e) => setUseCustomPrompt(e.target.checked)}
                    />
                  }
                  label="Use custom prompt"
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Custom Prompt"
                  placeholder="Example: Generate notes in Cornell format with key questions in the left margin..."
                  disabled={!useCustomPrompt || loading}
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  sx={{ mt: 2 }}
                  helperText={
                    !useCustomPrompt 
                      ? "Enable custom prompt to edit" 
                      : customPrompt.trim() 
                        ? "" 
                        : "Using default prompt: Create well-structured notes with headings and bullet points"
                  }
                />
              </Box>
            </Collapse>
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Generate Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerateNotes}
              disabled={loading || (!file && !text.trim())}
              startIcon={loading ? <CircularProgress size={20} /> : <Description />}
            >
              {loading ? 'Generating...' : 'Generate Notes'}
            </Button>
          </Box>

          {/* Generated Notes Display */}
          {notes && (
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Generated Notes
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleUndoEdit}
                    disabled={currentEditIndex <= 0}
                    startIcon={<Undo />}
                  >
                    Undo
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleRedoEdit}
                    disabled={currentEditIndex >= editHistory.length - 1}
                    startIcon={<Redo />}
                  >
                    Redo
                  </Button>
                  {notes !== originalNotes && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleRevertChanges}
                      disabled={editingNotes}
                    >
                      Revert Changes
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleEditNotes}
                    startIcon={<Edit />}
                    disabled={loading || editingNotes}
                  >
                    Edit Notes
                  </Button>
                  {isPro && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleExport}
                      startIcon={<FileDownload />}
                    >
                      Export
                    </Button>
                  )}
                  {user && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveAlt />}
                      onClick={async () => {
                        try {
                          await saveNote(user.id, notes);
                          setSnackbarMessage('Notes saved successfully!');
                          setSnackbarOpen(true);
                        } catch (err) {
                          setError('Failed to save notes');
                          console.error('Save error:', err);
                        }
                      }}
                      disabled={editingNotes}
                    >
                      Save Notes
                    </Button>
                  )}
                </Stack>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {editingNotes ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    backgroundColor: 'background.default',
                    p: 3,
                    borderRadius: 1,
                    '& h1': { 
                      fontSize: '2em',
                      fontWeight: 'bold',
                      color: 'primary.main',
                      mt: 3,
                      mb: 2,
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      pb: 1
                    },
                    '& h2': { 
                      fontSize: '1.5em',
                      fontWeight: 'bold',
                      color: 'secondary.main',
                      mt: 2,
                      mb: 1.5
                    },
                    '& h3': { 
                      fontSize: '1.25em',
                      fontWeight: 'bold',
                      color: 'text.primary',
                      mt: 2,
                      mb: 1
                    },
                    '& p': {
                      fontSize: '1.1em',
                      lineHeight: 1.6,
                      mb: 1.5
                    },
                    '& ul, & ol': {
                      pl: 2,
                      mb: 2,
                      '& li': {
                        mb: 1,
                        '& p': { mb: 0 }
                      }
                    },
                    '& blockquote': {
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                      pl: 2,
                      py: 1,
                      my: 2,
                      backgroundColor: 'action.hover',
                      fontStyle: 'italic'
                    },
                    '& code': {
                      backgroundColor: 'action.hover',
                      p: 0.5,
                      borderRadius: 1,
                      fontFamily: 'monospace'
                    },
                    '& pre': {
                      backgroundColor: 'action.hover',
                      p: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      mb: 2
                    },
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      mb: 2,
                      '& th, & td': {
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 1,
                        textAlign: 'left'
                      },
                      '& th': {
                        backgroundColor: 'action.hover',
                      fontWeight: 'bold'
                      }
                    },
                    '& hr': {
                      border: 'none',
                      borderTop: '2px solid',
                      borderColor: 'divider',
                      my: 2
                    },
                    '& strong': {
                      fontWeight: 'bold',
                      color: 'primary.main'
                    },
                    '& em': {
                      fontStyle: 'italic',
                      color: 'text.secondary'
                    }
                  }}
                >
                  <ReactMarkdown
                    components={{
                      h1: ({node, ...props}) => <Typography variant="h1" {...props} />,
                      h2: ({node, ...props}) => <Typography variant="h2" {...props} />,
                      h3: ({node, ...props}) => <Typography variant="h3" {...props} />,
                      p: ({node, ...props}) => <Typography variant="body1" {...props} />,
                      ul: ({node, ...props}) => <Typography component="ul" {...props} />,
                      ol: ({node, ...props}) => <Typography component="ol" {...props} />,
                      li: ({node, ...props}) => <Typography component="li" {...props} />,
                      blockquote: ({node, ...props}) => <Typography component="blockquote" {...props} />,
                      code: ({node, inline, ...props}) => 
                        inline ? <Typography component="code" {...props} /> : <Typography component="pre" {...props} />,
                      pre: ({node, ...props}) => <Typography component="pre" {...props} />,
                      table: ({node, ...props}) => <Typography component="table" {...props} />,
                      thead: ({node, ...props}) => <Typography component="thead" {...props} />,
                      tbody: ({node, ...props}) => <Typography component="tbody" {...props} />,
                      tr: ({node, ...props}) => <Typography component="tr" {...props} />,
                      th: ({node, ...props}) => <Typography component="th" {...props} />,
                      td: ({node, ...props}) => <Typography component="td" {...props} />,
                      hr: ({node, ...props}) => <Divider {...props} />,
                      strong: ({node, ...props}) => <Typography component="strong" {...props} />,
                      em: ({node, ...props}) => <Typography component="em" {...props} />
                    }}
                  >
                    {notes}
                  </ReactMarkdown>
                </Box>
              )}
            </Paper>
          )}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
          />
        </Stack>
      </Container>

      {/* Edit Notes Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Notes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide instructions on how you'd like to edit your notes. For example: "Add a section on key concepts", "Reorganize by topics", or "Make it more concise".
          </Typography>
          <TextField
            autoFocus
            label="Edit Instructions"
            fullWidth
            multiline
            rows={4}
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            placeholder="Example: Simplify the language and add more examples"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleEditNotesSubmit} 
            color="primary" 
            variant="contained"
            disabled={!editPrompt.trim()}
          >
            Apply Edits
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feature Comparison Dialog */}
      <Dialog 
        open={showFeatureComparison} 
        onClose={() => setShowFeatureComparison(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Feature Comparison</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Feature</TableCell>
                  <TableCell align="center">Free</TableCell>
                  <TableCell align="center">Pro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>File Size Limit</TableCell>
                  <TableCell align="center">10MB</TableCell>
                  <TableCell align="center">500MB</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Processing Time</TableCell>
                  <TableCell align="center">30 seconds</TableCell>
                  <TableCell align="center">500 seconds</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Note Templates</TableCell>
                  <TableCell align="center">Basic</TableCell>
                  <TableCell align="center">Custom</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Export Formats</TableCell>
                  <TableCell align="center">Basic</TableCell>
                  <TableCell align="center">Multiple</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Processing Speed</TableCell>
                  <TableCell align="center">Standard</TableCell>
                  <TableCell align="center">Fast</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFeatureComparison(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ position: 'relative' }}>
        {!isPro && (
          <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
            <Chip 
              label="Free Version"
              color="default"
              icon={<Info />}
            />
          </Box>
        )}
        {isPro && (
          <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
            <Chip 
              label="Pro Features Active"
              color="success"
              icon={<Info />}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default NotesGenerator;
